/**
 * PR-7b — Estilos/formato del Excel de agencia, extraídos de
 * `LiquidacionesAgenciaComponent` (`aplicarEstilosAhojaExcel`, `capitalizeHeader`,
 * `getColumnWidth`, `filtrarColumnasConServiciosPresentes`).
 *
 * Puro — sin Angular ni HTTP — para poder probarse sin TestBed, igual que
 * `liquidaciones-agencia-excel-columnas.util.ts`. El comportamiento es
 * BYTE-IDÉNTICO al del componente: ver
 * `liquidaciones-agencia.component.excel-characterization.spec.ts` (PR-7a), que
 * arma el workbook real llamando al componente y verifica hojas/headers/filas/
 * merges/anchos/fills/fonts/numFmt sin que este archivo lo toque.
 */
import * as ExcelJS from 'exceljs';
import {
    esColumnaTotalizable,
    humanizarClave,
} from './liquidaciones-agencia-excel-columnas.util';

export interface AgenciaColumnHeader {
    header: string;
    key: string;
    width: number;
}

/**
 * Aplica estilos a una hoja Excel (reutilizado para Excel individual y
 * fusionado): logo, título, fecha de generación, headers, filas (con
 * alternancia de color y fila de TOTALES destacada) y la fila de TOTALES
 * sintética cuando los datos no la traían.
 *
 * `onLogoError` reemplaza al `LoggerService` del componente — este archivo no
 * depende de Angular, así que el logging queda a cargo de quien llama (ver el
 * delegador en el componente).
 */
export async function aplicarEstilosAhojaExcel(
    workbook: ExcelJS.Workbook,
    worksheet: ExcelJS.Worksheet,
    titulo: string,
    rows: any[],
    columnHeaders: AgenciaColumnHeader[],
    colors: Record<string, string>,
    logoArrayBuffer: ArrayBuffer | null,
    onLogoError?: (error: unknown) => void
): Promise<void> {
    if (logoArrayBuffer) {
        try {
            const logoId = workbook.addImage({
                buffer: logoArrayBuffer,
                extension: 'png',
            });
            worksheet.addImage(logoId, {
                tl: { col: 0.2, row: 0.2 },
                ext: { width: 150, height: 50 },
            });
        } catch (error) {
            onLogoError?.(error);
        }
    }
    const titleRow = worksheet.getRow(1);
    titleRow.height = 40;
    const titleCell = worksheet.getCell('D1');
    titleCell.value = titulo.toUpperCase();
    titleCell.font = {
        name: 'Calibri',
        size: 20,
        bold: true,
        color: { argb: 'FF' + colors['primary'] },
    };
    titleCell.alignment = {
        vertical: 'middle',
        horizontal: 'left',
    };
    worksheet.mergeCells('D1:H1');
    const dateRow = worksheet.getRow(2);
    dateRow.height = 25;
    const dateCell = worksheet.getCell('D2');
    dateCell.value = `Generado el: ${new Date().toLocaleDateString('es-CL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })}`;
    dateCell.font = {
        name: 'Calibri',
        size: 11,
        color: { argb: 'FF' + colors['gray'] },
    };
    dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
    worksheet.mergeCells('D2:H2');
    worksheet.getRow(3).height = 10;
    columnHeaders.forEach((col, index) => {
        worksheet.getColumn(index + 1).width = col.width;
    });
    const headerRow = worksheet.getRow(4);
    headerRow.height = 30;
    columnHeaders.forEach((col, index) => {
        const cell = worksheet.getCell(4, index + 1);
        cell.value = col.header;
        cell.font = {
            name: 'Calibri',
            size: 12,
            bold: true,
            color: { argb: 'FFFFFFFF' },
        };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF' + colors['primary'] },
        };
        cell.alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true,
        };
        cell.border = {
            top: {
                style: 'medium',
                color: { argb: 'FF' + colors['dark'] },
            },
            bottom: {
                style: 'medium',
                color: { argb: 'FF' + colors['dark'] },
            },
            left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
        };
    });
    rows.forEach((item, index) => {
        const rowNumber = 5 + index;
        const row = worksheet.getRow(rowNumber);
        const esFilaTotales =
            item.numeroLiquidacion === 'Totales' ||
            item.numeroLiquidacion === 'TOTALES' ||
            Object.values(item).some(
                (val) => val === 'Totales' || val === 'TOTALES'
            );
        columnHeaders.forEach((col, colIndex) => {
            const cell = row.getCell(colIndex + 1);
            const value = item[col.key];
            cell.value = value !== undefined && value !== null ? value : '';
            if (esFilaTotales) {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF' + colors['dark'] },
                };
                cell.font = {
                    name: 'Calibri',
                    size: 12,
                    bold: true,
                    color: { argb: 'FFFFFFFF' },
                };
            } else {
                const fillColor =
                    index % 2 === 0
                        ? { argb: 'FFFFFFFF' }
                        : { argb: 'FF' + colors['light'] };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: fillColor,
                };
                cell.font = {
                    name: 'Calibri',
                    size: 11,
                    color: { argb: 'FF000000' },
                };
            }
            if (typeof value === 'number') {
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: 'right',
                };
                cell.numFmt = '#,##0';
            } else {
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: 'left',
                };
            }
            cell.border = {
                top: { style: 'thin', color: { argb: 'FFd1d5db' } },
                bottom: {
                    style: 'thin',
                    color: { argb: 'FFd1d5db' },
                },
                left: {
                    style: 'thin',
                    color: { argb: 'FFd1d5db' },
                },
                right: {
                    style: 'thin',
                    color: { argb: 'FFd1d5db' },
                },
            };
        });
        row.height = esFilaTotales ? 25 : 22;
    });

    // MEJ-03: fila de TOTALES al cierre de cada hoja (suma de columnas
    // numéricas). Solo si los datos no traían ya una fila de totales.
    const yaTieneFilaTotales = rows.some(
        (item) =>
            item?.numeroLiquidacion === 'Totales' ||
            item?.numeroLiquidacion === 'TOTALES' ||
            Object.values(item || {}).some(
                (val) => val === 'Totales' || val === 'TOTALES'
            )
    );
    if (rows.length > 0 && !yaTieneFilaTotales) {
        const sums: Record<string, number> = {};
        columnHeaders.forEach((col) => {
            // Los IDENTIFICADORES numéricos no se totalizan: sumar un ID da un
            // número sin significado. Caso real (liq 1612): la columna
            // "Transport Order ID" mostraba 10.611 = 3.537 sumado 3 veces, una
            // por contenedor. Igual en Agenciamiento con "nroIE".
            if (!esColumnaTotalizable(col.key)) {
                return;
            }
            const esNumerica = rows.some(
                (it) => typeof it[col.key] === 'number'
            );
            if (esNumerica) {
                sums[col.key] =
                    Math.round(
                        rows.reduce(
                            (acc, it) =>
                                acc +
                                (typeof it[col.key] === 'number'
                                    ? it[col.key]
                                    : 0),
                            0
                        ) * 100
                    ) / 100;
            }
        });
        const totalRow = worksheet.getRow(5 + rows.length);
        columnHeaders.forEach((col, colIndex) => {
            const cell = totalRow.getCell(colIndex + 1);
            if (colIndex === 0) {
                cell.value = 'TOTALES';
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: 'left',
                };
            } else if (col.key in sums) {
                cell.value = sums[col.key];
                cell.numFmt = '#,##0';
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: 'right',
                };
            } else {
                cell.value = '';
            }
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF' + colors['dark'] },
            };
            cell.font = {
                name: 'Calibri',
                size: 12,
                bold: true,
                color: { argb: 'FFFFFFFF' },
            };
            cell.border = {
                top: {
                    style: 'medium',
                    color: { argb: 'FF' + colors['dark'] },
                },
                bottom: {
                    style: 'medium',
                    color: { argb: 'FF' + colors['dark'] },
                },
                left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            };
        });
        totalRow.height = 25;
    }
}

/**
 * Rótulos EXACTOS para las columnas del Excel de agencia (Totales, Agenciamiento,
 * Pallets, Servicios Extra, Transportes).
 *
 * `export` solo para que el spec unitario (PR-7o) pueda recorrer el mapa
 * completo con `Object.entries` y fijar cada rótulo por `test.each`, sin
 * duplicarlo a mano. No cambia el comportamiento de `capitalizeHeader`.
 */
export const HEADER_MAP: { [key: string]: string } = {
    // Campos de Agenciamiento (V2)
    nroLiquidacion: 'Nro. Liquidación',
    // Punto C (OPB, ago-2026): "Deben tener el mismo formato y nombres de
    // columnas". El Consolidado ya rotulaba "N° Factura" / "Nota de Cobro"
    // (headers hardcodeados) mientras Agenciamiento y Transportes mostraban
    // "Nro Factura Venta" / "Nro Nota de Cobro" desde acá. Como las tres hojas
    // comparten estas claves, unificar el mapa alinea las dos que faltaban.
    // ⚠️ Se cambia el RÓTULO, nunca la CLAVE: `nroFacturaVenta` y `nroNotaCobro`
    // están en COLUMNAS_NO_TOTALIZABLES; si cambia la clave, se caen de esa
    // lista y vuelve el bug de sumar identificadores en la fila de TOTALES.
    nroFacturaVenta: 'N° Factura',
    nroNotaCobro: 'Nota de Cobro',
    nroIE: 'Nro. I/E',
    numeroBl: 'B/L',
    reserva: 'Reserva',
    contenedor: 'Contenedores',
    cliente: 'Cliente',
    refCliente: 'Ref. Cliente',
    nroDespacho: 'Nro. Despacho',
    dus: 'DUS',
    estadoDus: 'Estado DUS',
    aceptacionDus: 'Aceptación DUS',
    prorrogaDus: 'Prórroga DUS',
    nave: 'Nave',
    nroViaje: 'Nro. Viaje',
    naviera: 'Naviera',
    pol: 'POL',
    pod: 'POD',
    paisDestinoFinal: 'País Destino Final',
    etd: 'ETD',
    mes: 'Mes',
    consignatario: 'Consignatario',
    tipoEmbarque: 'Tipo Embarque',
    sConv: 'S/Conv',
    cntrs: 'CNTRS',
    tipoBultoCntr: 'Tipo Bulto CNTR',
    plts: 'PLTS',
    cajas: 'Cajas',
    especie: 'Especie',
    condicion: 'Condición',
    tipoEmbalaje: 'Tipo Embalaje',
    kilosNetos: 'Kilos Netos',
    kilosBrutos: 'Kilos Brutos',
    viaTransporte: 'Vía Transporte',
    agenciamiento: 'Agenciamiento',
    ucco: 'UCCO',
    facturaUcco: 'Nº Documento',
    uccoChinaEur: 'UCCO (CHINA-EUR)',
    facturaUccoChinaEur: 'Nº Documento',
    uccoFgn: 'UCCO (FGN)',
    facturaUccoFgn: 'Nº Documento',
    extraUcco: 'Extra UCCO',
    facturaExtraUcco: 'Nº Documento',
    fito: 'FITO',
    facturaFito: 'Factura FITO',
    extraFito: 'Extra FITO',
    facturaExtraFito: 'Factura Extra FITO',
    controlSello: 'Control Sello',
    correccionBl: 'Corrección de BL',
    facturaCorreccionBl: 'Factura Corrección BL',
    dusLegalizado: 'DUS Legalizado',
    facturaDus: 'Factura DUS',
    excesoRecursosPallet: 'Exceso de Recursos Pallet',
    facturaExcesoRecursosPallet: 'Factura Exceso de Recursos Pallet',
    liberacionViaEmail: 'Liberación Vía Email',
    facturaLiberacionViaEmail: 'Factura Liberación Vía Email',
    matrizFueraPlazo: 'Matriz Fuera de Plazo',
    facturaMatrizFueraPlazo: 'Factura Matriz Fuera de Plazo',
    multaAduanaPlts: 'Multa Aduana PLTS',
    facturaMultaAduanaPlts: 'Factura Multa Aduana PLTS',
    // Servicios tardíos (Traspaso) — Agenciamiento + Transporte
    facturaLateArrival: 'Factura Late Arrival',
    extraLateArrival: 'Extra Late Arrival',
    facturaExtraLateArrival: 'Factura Extra Late Arrival',
    ingresoPuertoTardio: 'Ingreso en Puerto Tardío',
    facturaIngresoPuertoTardio: 'Factura Ingreso en Puerto Tardío',
    pagoTardioBlExport: 'Pago Tardío de BL Export',
    facturaPagoTardioBlExport: 'Factura Pago Tardío de BL Export',
    securityFeeContenedor: 'Security Fee Contenedor',
    facturaSecurityFee: 'Factura Security Fee',
    gestionOpb: 'Gestión OPB',
    totalVenta: 'Total Venta',
    totalTraspaso: 'Total Traspaso',
    // Campos legacy V1
    numeroLiquidacion: 'N° Liquidación',
    nroContenedor: 'N° Contenedor',
    transporte: 'Transporte',
    servicioGateOut: 'Servicio Gate Out',
    gestionOpbTraspaso: 'Gestión OPB Traspaso',
    totalTraspasoBruto: 'Total Traspaso Bruto',
    totalVentaNeto: 'Total Venta Neto',
    gate: 'Gate',
    totalNeto: 'Total Neto',
    exportador: 'Exportador',
    pesoKg: 'Peso (Kg)',
    volumenM3: 'Volumen (M³)',
    tipoServicio: 'Tipo Servicio',
    observaciones: 'Observaciones',
    // Campos de Transportes (V2) - Solo los que no están arriba
    liquidacion: 'Liquidación',
    ot: 'OT',
    estado: 'Estado',
    booking: 'Booking',
    retiroContenedor: 'Retiro Contenedor',
    deposito: 'Depósito',
    lugarOrigen: 'Lugar Origen',
    planta: 'Planta',
    terminal: 'Terminal',
    container: 'Contenedor',
    sello: 'Sello',
    pallets: 'Pallets',
    tipoContenedor: 'Tipo Contenedor',
    costo: 'Costo',
    shipConv: 'Ship Conv',
    // Punto C (OPB, ago-2026): estas dos columnas ya NO se emiten en la hoja
    // Transportes (ver el transform en liquidaciones.service.ts). Los rótulos
    // quedan sólo como fallback por si alguna otra superficie usa las claves.
    transportOrderId: 'Transport Order ID',
    containerNumber: 'Container Number',
    sobrestadiaPuerto: 'Sobrestadía Puerto',
    porteos: 'Porteos',
    retiroCruzado: 'Retiro Cruzado',
    sobrestadiaPlanta: 'Sobrestadía Planta',
    almacenaje: 'Almacenaje',
    falsoFleteDeposito: 'Falso Flete Depósito',
    sobrestadiaDeposito: 'Sobrestadía Depósito',
    falsoFletePlanta: 'Falso Flete Planta',
    habilitacionDeposito: 'Habilitación Depósito',
    interplanta: 'Interplanta',
    rotuladoCargaImo: 'Rotulado Carga IMO',
    servicioGateIn: 'Servicio Gate In',
    extraUnderslung: 'Extra Underslung',
    generadorConexion: 'Generador/Conexión',
    multaPesaje: 'Multa Pesaje',
    manipuleo: 'Manipuleo',
    preAsignacion: 'Pre-Asignación',
    cambioSellos: 'Cambio Sellos',
    porteoEntregaZeal: 'Porteo Entrega Zeal',
    porteoFull: 'Porteo Full',
    segundoTramoExtraCntr: '2do Tramo Extra CNTR',
    segundoTramoExtraPlts: '2do Tramo Extra PLTS',
    lateArrival: 'Late Arrival',
    falsoFleteRutaPlanta: 'Falso Flete Ruta Planta',
    gestionAnulacionGateOut: 'Gestión Anulación Gate Out',
    diferenciaTarifaFlete: 'Diferencia Tarifa Flete',
    falsoFleteFullRetorno: 'Falso Flete Full Retorno',
    devolucionDeposito: 'Devolución en Depósito',
    sobrepesoCarga: 'Sobrepeso de Carga',
    // Campos de servicios extra
    nroOs: 'N° OS',
    servicio: 'Servicio',
    tipoFacturacion: 'Tipo Facturación',
    cantidad: 'Cantidad',
    gestionOpbNeto: 'Gestión OPB Neto',
    comentarios: 'Comentarios',
    // Campos de Consolidado (V2)
    costoTransporte: 'Costo Transporte',
    gateOut: 'Gate Out',
    gateIn: 'Gate In',
    dusNoLegalizado: 'DUS no legalizado',
    totalVentaAgenciamiento: 'Total Venta Agenciamiento',
    totalTraspasoAgenciamiento: 'Total Traspaso Agenciamiento',
    totalVentaTransporte: 'Total Venta Transporte',
    totalTraspasoTransporte: 'Total Traspaso Transporte',
    totalGeneral: 'Total General',
};

/**
 * Capitaliza/rotula una clave de columna para mostrarla como header del
 * Excel. Fallback para las columnas que no están en `HEADER_MAP`: desde el
 * pase a columnas dinámicas son las de los servicios nuevos, y antes se
 * mostraban en crudo (`ServicioGateOutV2` en el Excel real de la liq 1606).
 */
export function capitalizeHeader(key: string): string {
    return HEADER_MAP[key] || humanizarClave(key);
}

/**
 * Anchos de columna EXACTOS para las hojas del Excel de agencia. Default: 15.
 *
 * `export` solo para cobertura exhaustiva por `test.each` en el spec (PR-7o) —
 * ver el comentario de `HEADER_MAP`.
 */
export const WIDTH_MAP: { [key: string]: number } = {
    numeroLiquidacion: 15,
    nave: 20,
    pol: 15,
    etd: 12,
    nroContenedor: 18,
    refCliente: 15,
    numeroBl: 18,
    plts: 10,
    agenciamiento: 15,
    transporte: 15,
    servicioGateOut: 15,
    dusLegalizado: 15,
    securityFeeContenedor: 18,
    gestionOpbTraspaso: 18,
    totalTraspasoBruto: 18,
    totalVentaNeto: 18,
    gate: 15,
    totalNeto: 18,
    exportador: 25,
    especie: 20,
    pesoKg: 12,
    volumenM3: 12,
    tipoServicio: 15,
    observaciones: 30,
    nroOs: 12,
    servicio: 25,
    tipoFacturacion: 15,
    cantidad: 10,
    gestionOpbNeto: 18,
    comentarios: 40,
    // Campos de Consolidado
    costoTransporte: 18,
    gateOut: 12,
    totalVentaAgenciamiento: 22,
    totalTraspasoAgenciamiento: 22,
    totalVentaTransporte: 20,
    totalTraspasoTransporte: 22,
    totalGeneral: 18,
};

/** Determina el ancho de columna para una clave dada. Default: 15. */
export function getColumnWidth(key: string): number {
    return WIDTH_MAP[key] || 15;
}

/**
 * Filtra las columnas para mostrar solo aquellas con servicios presentes.
 * Mantiene columnas básicas identificadoras y filtra dinámicamente las de servicios.
 *
 * @param allKeys Todas las claves de columnas
 * @param serviciosPresentes Lista de servicios con monto > 0
 * @returns Array filtrado de claves de columnas
 */
export function filtrarColumnasConServiciosPresentes(
    allKeys: string[],
    serviciosPresentes: string[]
): string[] {
    // Columnas que SIEMPRE deben aparecer (no son dinámicas)
    const columnasBasicas = new Set([
        // Agenciamiento
        'nroLiquidacion',
        'nroFacturaVenta',
        'nroNotaCobro',
        'nroIE',
        'numeroBl',
        'reserva',
        'contenedor',
        'cliente',
        'refCliente',
        'nroDespacho',
        'dus',
        'estadoDus',
        'aceptacionDus',
        'prorrogaDus',
        'nave',
        'nroViaje',
        'naviera',
        'pol',
        'pod',
        'paisDestinoFinal',
        'etd',
        'mes',
        'consignatario',
        'tipoEmbarque',
        'sConv',
        'cntrs',
        'tipoBultoCntr',
        'plts',
        'cajas',
        'especie',
        'condicion',
        'tipoEmbalaje',
        'kilosNetos',
        'kilosBrutos',
        'viaTransporte',
        'totalVenta',
        'totalTraspaso',
        // Transportes
        'liquidacion',
        'ot',
        'estado',
        'exportador',
        'booking',
        'retiroContenedor',
        'deposito',
        'lugarOrigen',
        'planta',
        'terminal',
        'container',
        'sello',
        'pallets',
        'tipoContenedor',
        'costo',
        'shipConv',
        // Punto C (OPB, ago-2026): 'transportOrderId' y 'containerNumber' salieron
        // del transform de la hoja Transportes, así que ya no llegan como claves.
        'gestionOpb',
    ]);

    // Servicios presentes (convertir a mayúsculas para comparación)
    const serviciosSet = new Set(
        serviciosPresentes.map((s) => s.toLowerCase())
    );

    // Mapeo de servicios a sus campos asociados (incluyendo facturas/documentos)
    const serviciosConFacturas: { [key: string]: string[] } = {
        gateout: [
            'gateOut',
            'servicioGateOut',
            'nroFacturaVenta',
            'nroNotaCobro',
        ],
        gatein: ['gateIn', 'servicioGateIn', 'nroFacturaVenta', 'nroNotaCobro'],
        habilitacionsag: ['habilitacionSag', 'nroFacturaVenta', 'nroNotaCobro'],
        ucco: ['ucco', 'facturaUcco'],
        uccochinaeur: ['uccoChinaEur', 'facturaUccoChinaEur'],
        uccofgn: ['uccoFgn', 'facturaUccoFgn'],
        extraucco: ['extraUcco', 'facturaExtraUcco'],
        fito: ['fito', 'facturaFito'],
        extrafito: ['extraFito', 'facturaExtraFito'],
        controlsello: ['controlSello', 'nroFacturaVenta', 'nroNotaCobro'],
        correccionbl: ['correccionBl', 'facturaCorreccionBl'],
        dusnolegalizado: ['dusNoLegalizado', 'nroFacturaVenta', 'nroNotaCobro'],
        duslegalizado: ['dusLegalizado', 'facturaDus'],
        excesorecursospallet: [
            'excesoRecursosPallet',
            'facturaExcesoRecursosPallet',
        ],
        liberacionviaemail: ['liberacionViaEmail', 'facturaLiberacionViaEmail'],
        matrizfueraplazo: ['matrizFueraPlazo', 'facturaMatrizFueraPlazo'],
        multaaduanaplts: ['multaAduanaPlts', 'facturaMultaAduanaPlts'],
        diferenciatarifaflete: [
            'diferenciaTarifaFlete',
            'facturaDiferenciaTarifaFlete',
        ],
        latearrival: ['lateArrival', 'facturaLateArrival'],
        extralatearrival: ['extraLateArrival', 'facturaExtraLateArrival'],
        ingresopuertotardio: [
            'ingresoPuertoTardio',
            'facturaIngresoPuertoTardio',
        ],
        pagotardioblexport: ['pagoTardioBlExport', 'facturaPagoTardioBlExport'],
        securityfeecontenedor: ['securityFeeContenedor', 'facturaSecurityFee'],
        // Transportes
        sobrestadiapuerto: ['sobrestadiaPuerto', 'nroFacturaVenta'],
        porteos: ['porteos', 'nroFacturaVenta'],
        retirocruzado: ['retiroCruzado', 'nroFacturaVenta'],
        sobrestadiaplanta: ['sobrestadiaPlanta', 'nroFacturaVenta'],
        almacenaje: ['almacenaje', 'nroFacturaVenta'],
        falsofletedeposito: ['falsoFleteDeposito', 'nroFacturaVenta'],
        sobrestadiadeposito: ['sobrestadiaDeposito', 'nroFacturaVenta'],
        falsofleteplanta: ['falsoFletePlanta', 'nroFacturaVenta'],
        habilitaciondeposito: ['habilitacionDeposito', 'nroFacturaVenta'],
        interplanta: ['interplanta', 'nroFacturaVenta'],
        rotuladocargaimo: ['rotuladoCargaImo', 'nroFacturaVenta'],
        extraunderslung: ['extraUnderslung', 'nroFacturaVenta'],
        generadorconexion: ['generadorConexion', 'nroFacturaVenta'],
        multapesaje: ['multaPesaje', 'nroFacturaVenta'],
        manipuleo: ['manipuleo', 'nroFacturaVenta'],
        preasignacion: ['preAsignacion', 'nroFacturaVenta'],
        cambiosellos: ['cambioSellos', 'nroFacturaVenta'],
        porteoentregazeal: ['porteoEntregaZeal', 'nroFacturaVenta'],
        porteofull: ['porteoFull', 'nroFacturaVenta'],
        segundotramoextracntr: ['segundoTramoExtraCntr', 'nroFacturaVenta'],
        segundotramoextraplts: ['segundoTramoExtraPlts', 'nroFacturaVenta'],
        falsofleterutaplanta: ['falsoFleteRutaPlanta', 'nroFacturaVenta'],
        gestionanulaciongateout: ['gestionAnulacionGateOut', 'nroFacturaVenta'],
        diferenciatarifalete: ['diferenciaTarifaFlete', 'nroFacturaVenta'],
        falsofletefulretorno: ['falsoFleteFullRetorno', 'nroFacturaVenta'],
        devoluciondeposito: ['devolucionDeposito', 'nroFacturaVenta'],
        sobrepesocarga: ['sobrepesoCarga', 'nroFacturaVenta'],
    };

    // Construir set de campos a incluir
    const camposAIncluir = new Set<string>();

    // Agregar columnas básicas
    columnasBasicas.forEach((col) => camposAIncluir.add(col));

    // Agregar campos de servicios presentes
    serviciosPresentes.forEach((servicio) => {
        const servicioLower = servicio.toLowerCase().replace(/\s+/g, '');
        const campos = serviciosConFacturas[servicioLower];
        if (campos) {
            campos.forEach((c) => camposAIncluir.add(c));
        } else {
            // Si no está en el mapeo, agregar el campo directo
            camposAIncluir.add(servicio);
        }
    });

    // Retornar solo las claves que están en el mapeo (allKeys ∩ camposAIncluir)
    return allKeys.filter((key) => camposAIncluir.has(key));
}
